import request from '../utils/apiClient';
import type {
  LoginData, UserInfo, UserListParams, CreateUserParams, UpdateUserParams,
  PermissionConfigItem, PermissionRoleItem, CreateRoleParams,
  BranchItem, BranchTreeItem, CreateBranchParams,
  EnrollmentBatch, EnrollmentItem, CreateEnrollmentParams,
  StudentProfileItem, CreateStudentParams, SelectionScoreItem, StudentStatusStats, StudentImportResult,
  GradeBatchItem, GradeRecordItem, GradeStatistics,
  NoticeItem,
  OperationLogItem,
  DashboardSummary,
  TeacherDashboardSummary,
  StudentDashboardSummary,
  CourseItem, CourseCreateParams, CourseEnrollmentItem,
  CourseSessionItem, SessionAttendanceItem, HomeworkItem, HomeworkSubmissionItem,
  ResearchProjectItem, ResearchProjectDetailItem, ResearchSubmissionItem,
  ActivityItem, ActivityCreateParams, ActivityParticipantItem, ActivityAttendanceItem, ActivitySubmissionItem,
  CompetitionItem, CompetitionCreateParams, CompetitionParticipantItem,
  CompetitionScoreItem, ScoreRecordParams, MyCompetitionScoreItem,
  DashboardData,
  ApiResponse,
  PaginatedResponse,
} from './types';

export interface LoginParams {
  username: string;
  password: string;
}

/**
 * 分页接口全量拉取工具。
 *
 * 后端默认分页为 20 条/页（StandardPagination），而花名册、考勤、成绩录入等
 * 场景需要课程的**全部**学生。这里按页循环拉取，避免"只显示第一页"的截断问题。
 */
async function fetchAllPages<T>(
  url: string,
  params: Record<string, string | number | boolean> = {},
): Promise<T[]> {
  const pageSize = 200; // 后端 max_page_size = 200
  const all: T[] = [];
  let page = 1;
  let totalPages = 1;
  // do-while：至少请求一次；total_pages 为 1 时直接结束
  do {
    const res: any = await request.get<unknown, PaginatedResponse<T>>(url, {
      params: { ...params, page, page_size: pageSize },
    });
    const data = res?.data;
    // 后端未分页时 data 直接是数组，兜底处理
    if (Array.isArray(data)) return data as T[];
    if (!data) break;
    all.push(...(data.results || []));
    totalPages = data.total_pages || 1;
    page += 1;
  } while (page <= totalPages);
  return all;
}

export const authApi = {
  login: (params: LoginParams) =>
    request.post<unknown, ApiResponse<LoginData>>('/auth/login/', params),

  logout: (refresh: string) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>('/auth/logout/', { refresh }),

  getMe: () =>
    request.get<unknown, ApiResponse<UserInfo>>('/auth/users/me/'),

  getDashboard: () =>
    request.get<unknown, ApiResponse<DashboardData>>('/auth/dashboard/'),

  getUsers: (params?: UserListParams) =>
    request.get<unknown, PaginatedResponse<UserInfo>>('/auth/users/', { params }),

  createUser: (data: CreateUserParams) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>('/auth/users/', data),

  updateUser: (id: number, data: UpdateUserParams) =>
    request.patch<unknown, ApiResponse<Record<string, unknown>>>(`/auth/users/${id}/`, data),

  toggleActive: (id: number) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>(`/auth/users/${id}/toggle_active/`),

  resetPassword: (id: number, new_password: string) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>(`/auth/users/${id}/reset_password/`, { new_password }),

  changePassword: (old_password: string, new_password: string) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>('/auth/users/change_password/', { old_password, new_password }),

  verifyPassword: (password: string) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>('/auth/users/verify_password/', { password }),

  deleteUser: (id: number) =>
    request.delete<unknown, ApiResponse<Record<string, unknown>>>(`/auth/users/${id}/`),

  getRoleOptions: () =>
    request.get<unknown, ApiResponse<Array<{ id: number; role_key: string; display_name: string }> > >('/auth/users/role_options/'),

  /** 操作日志（分页，每页条数由 page_size 控制） */
  getOperationLogs: (params?: { page?: number; page_size?: number; module?: string; search?: string }) =>
    request.get<unknown, PaginatedResponse<OperationLogItem>>('/auth/logs/operation/', { params }),

  /** 管理端看板聚合数据（KPI / 待办 / 分布 / 趋势 / 动态，服务端 60s 缓存） */
  getDashboardSummary: () =>
    request.get<unknown, ApiResponse<DashboardSummary>>('/auth/dashboard/summary/'),

  /** 教师看板聚合数据（限定「我教的课程」口径） */
  getTeacherSummary: () =>
    request.get<unknown, ApiResponse<TeacherDashboardSummary>>('/auth/dashboard/teacher_summary/'),

  /** 学生看板聚合数据（我的课程 / 待交作业 / 本周课表） */
  getStudentSummary: () =>
    request.get<unknown, ApiResponse<StudentDashboardSummary>>('/auth/dashboard/student_summary/'),
};

export const permissionApi = {
  list: (): Promise<ApiResponse<{
    modules: Array<{ value: string; label: string }>;
    roles: Array<{ value: string; label: string }>;
    configs: PermissionConfigItem[];
  }> > => request.get('/auth/permissions/'),
  batchUpdate: (configs: PermissionConfigItem[]) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>('/auth/permissions/batch_update/', { configs }),
  reset: () => request.post<unknown, ApiResponse<Record<string, unknown>>>('/auth/permissions/reset/'),
  // 角色管理
  listRoles: () => request.get<unknown, ApiResponse<PermissionRoleItem[]>>('/auth/permissions/roles/'),
  createRole: (data: CreateRoleParams) => request.post<unknown, ApiResponse<Record<string, unknown>>>('/auth/permissions/roles/create/', data),
  updateRole: (id: number, data: Partial<PermissionRoleItem>) =>
    request.put<unknown, ApiResponse<Record<string, unknown>>>(`/auth/permissions/roles/${id}/update/`, data),
  toggleRole: (id: number) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/auth/permissions/roles/${id}/toggle/`),
  deleteRole: (id: number) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/auth/permissions/roles/${id}/delete/`),
};

export const branchApi = {
  list: (params?: Record<string, string | number | boolean>) =>
    request.get<unknown, PaginatedResponse<BranchItem>>('/branches/', { params }),
  tree: () => request.get<unknown, ApiResponse<BranchTreeItem[]>>('/branches/tree/'),
  create: (data: CreateBranchParams) => request.post<unknown, ApiResponse<Record<string, unknown>>>('/branches/', data),
  update: (id: number, data: Partial<CreateBranchParams>) =>
    request.patch<unknown, ApiResponse<Record<string, unknown>>>(`/branches/${id}/`, data),
  toggleActive: (id: number) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/branches/${id}/toggle_active/`),
};

export const studentApi = {
  batches: {
    list: () => request.get<unknown, PaginatedResponse<EnrollmentBatch>>('/students/batches/'),
    create: (data: Partial<EnrollmentBatch>) => request.post<unknown, ApiResponse<Record<string, unknown>>>('/students/batches/', data),
    update: (id: number, data: Partial<EnrollmentBatch>) =>
      request.patch<unknown, ApiResponse<Record<string, unknown>>>(`/students/batches/${id}/`, data),
    destroy: (id: number, password: string) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>(`/students/batches/${id}/delete_batch/`, { password }),
    close: (id: number) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/students/batches/${id}/close/`),
    reopen: (id: number) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/students/batches/${id}/reopen/`),
    export: (params?: Record<string, unknown>) =>
      request.get<unknown, Blob>('/students/batches/export/', { params, responseType: 'blob' }),
  },
  enrollments: {
    list: (params?: Record<string, string | number | boolean>) =>
      request.get<unknown, PaginatedResponse<EnrollmentItem>>('/students/enrollments/', { params }),
    create: (data: CreateEnrollmentParams) => request.post<unknown, ApiResponse<Record<string, unknown>>>('/students/enrollments/', data),
    admit: (id: number) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/students/enrollments/${id}/admit/`),
    batchAdmit: (ids: number[]) => request.post<unknown, ApiResponse<Record<string, unknown>>>('/students/enrollments/batch_admit/', { ids }),
    export: (params?: Record<string, unknown>) =>
      request.get('/students/enrollments/export/', { params, responseType: 'blob' }),
  },
  selectionScores: {
    list: (params?: Record<string, string | number>) =>
      request.get<unknown, PaginatedResponse<SelectionScoreItem>>('/students/selection-scores/', { params }),
    create: (data: Partial<SelectionScoreItem>) => request.post<unknown, ApiResponse<Record<string, unknown>>>('/students/selection-scores/', data),
    update: (id: number, data: Partial<SelectionScoreItem>) =>
      request.patch<unknown, ApiResponse<Record<string, unknown>>>(`/students/selection-scores/${id}/`, data),
  },
  profiles: {
    list: (params?: Record<string, string | number | boolean>) =>
      request.get<unknown, PaginatedResponse<StudentProfileItem>>('/students/profiles/', { params }),
    /** 按学籍状态统计人数（在籍/已毕业/已退出），不受列表筛选条件影响 */
    statusStats: () =>
      request.get<unknown, ApiResponse<StudentStatusStats>>('/students/profiles/status_stats/'),
    get: (id: number) => request.get<unknown, ApiResponse<StudentProfileItem>>(`/students/profiles/${id}/`),
    create: (data: CreateStudentParams) => request.post<unknown, ApiResponse<Record<string, unknown>>>('/students/profiles/', data),
    update: (id: number, data: Partial<CreateStudentParams>) =>
      request.patch<unknown, ApiResponse<Record<string, unknown>>>(`/students/profiles/${id}/`, data),
    suspend: (id: number) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/students/profiles/${id}/suspend/`),
    batchSuspend: (ids: number[], targetStatus: string) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>('/students/profiles/batch_suspend/', { ids, target_status: targetStatus }),
    batchSetBranch: (ids: number[], branchIds: number[], mode: 'add' | 'remove' | 'set' = 'set') =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>('/students/profiles/batch_set_branch/', { ids, branch_ids: branchIds, mode }),
    uploadPhoto: (id: number, file: File) => {
      const fd = new FormData();
      fd.append('photo', file);
      return request.post<unknown, ApiResponse<{ photo_url: string }>>(
        `/students/profiles/${id}/upload_photo/`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
    },
    deleteConfirm: (id: number, password: string) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>(`/students/profiles/${id}/delete_confirm/`, { password }),
    batchDelete: (ids: number[], password: string) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>('/students/profiles/batch_delete/', { ids, password }),
    export: (params?: Record<string, unknown>) =>
      request.get<unknown, Blob>('/students/profiles/export/', { params, responseType: 'blob' }),
    importExcel: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return request.post<unknown, ApiResponse<StudentImportResult>>('/students/profiles/import_excel/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
  },
};

export const gradeApi = {
  batches: {
    list: (params?: Record<string, string | number>) =>
      request.get<unknown, PaginatedResponse<GradeBatchItem>>('/grades/batches/', { params }),
    get: (id: number) => request.get<unknown, ApiResponse<GradeBatchItem>>(`/grades/batches/${id}/`),
    create: (data: Partial<GradeBatchItem>) => request.post<unknown, ApiResponse<Record<string, unknown>>>('/grades/batches/', data),
    submit: (id: number) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/grades/batches/${id}/submit/`),
    withdraw: (id: number) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/grades/batches/${id}/withdraw/`),
    approve: (id: number, comment?: string) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/grades/batches/${id}/approve/`, { comment }),
    reject: (id: number, comment: string) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/grades/batches/${id}/reject/`, { comment }),
    publish: (id: number) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/grades/batches/${id}/publish/`),
  },
  records: {
    list: (params?: Record<string, string | number>) =>
      request.get<unknown, PaginatedResponse<GradeRecordItem>>('/grades/records/', { params }),
    bulkInput: (data: { course_id: number; grades: Array<{ student_id: number; score: number | null; note?: string }> }) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>('/grades/records/bulk_input/', data),
    statistics: (params?: Record<string, string | number>) =>
      request.get<unknown, ApiResponse<GradeStatistics>>('/grades/records/statistics/', { params }),
    export: (params?: Record<string, unknown>) =>
      request.get('/grades/records/export/', { params, responseType: 'blob' }),
  },
};

export const noticeApi = {
  list: (params?: Record<string, string | number | boolean>) =>
    request.get<unknown, PaginatedResponse<NoticeItem>>('/notices/', { params }),
  markRead: (id: number) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/notices/${id}/mark_read/`),
  markAllRead: () => request.post<unknown, ApiResponse<Record<string, unknown>>>('/notices/mark_all_read/'),
  unreadCount: () => request.get<unknown, ApiResponse<{ count: number }>>('/notices/unread_count/'),
};

export const courseApi = {
  list: (params?: Record<string, string | number | boolean>) =>
    request.get<unknown, PaginatedResponse<CourseItem>>('/courses/', { params }),
  get: (id: number) => request.get<unknown, ApiResponse<CourseItem>>(`/courses/${id}/`),
  create: (data: CourseCreateParams) => request.post<unknown, ApiResponse<Record<string, unknown>>>('/courses/', data),
  update: (id: number, data: Partial<CourseCreateParams>) =>
    request.patch<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${id}/`, data),
  delete: (id: number) => request.delete<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${id}/`),
  close: (id: number) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${id}/close/`),
  reopen: (id: number) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${id}/reopen/`),
  enrollments: {
    list: (params?: Record<string, string | number | boolean>) =>
      request.get<unknown, PaginatedResponse<CourseEnrollmentItem>>('/courses/enrollments/', { params }),
    /** 拉取某门课程的**全部**选课学生（自动翻页，避免默认 20 条截断） */
    listAll: (course: number) =>
      fetchAllPages<CourseEnrollmentItem>('/courses/enrollments/', { course }),
    create: (data: { course: number; student?: number }) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>('/courses/enrollments/', data),
    delete: (id: number) => request.delete<unknown, ApiResponse<Record<string, unknown>>>(`/courses/enrollments/${id}/`),
  },
  sessions: {
    list: (courseId: number) =>
      request.get<unknown, ApiResponse<CourseSessionItem[]>>(`/courses/${courseId}/course_sessions/`),
    create: (courseId: number, data: Partial<CourseSessionItem>) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/add_session/`, data),
    batchCreate: (courseId: number, data: {
      start_date: string; end_date: string; week_days: number[];
      start_time?: string; end_time?: string; topic?: string; location?: string;
    }) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/batch_add_sessions/`, data, { timeout: 60000 }),
    remove: (courseId: number, sessionId: number) =>
      request.delete<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/remove_session/?session_id=${sessionId}`),
    batchRemove: (courseId: number, sessionIds: number[]) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/batch_remove_sessions/`, { session_ids: sessionIds }),
    batchSetTopic: (courseId: number, sessionIds: number[], topic: string) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/batch_set_topic/`, { session_ids: sessionIds, topic }),
    /** 单独修改某一次上课活动的信息（日期/上下课时间/地点/主题） */
    update: (courseId: number, sessionId: number, data: Partial<CourseSessionItem>) =>
      request.post<unknown, ApiResponse<CourseSessionItem>>(`/courses/${courseId}/update_session/`, { session_id: sessionId, ...data }),
    /** 批量设置所选上课活动的上下课时间 */
    batchSetTime: (courseId: number, sessionIds: number[], data: { start_time: string; end_time: string }) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/batch_set_time/`, { session_ids: sessionIds, ...data }),
    /** 批量设置所选上课活动的上课地点 */
    batchSetLocation: (courseId: number, sessionIds: number[], location: string) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/batch_set_location/`, { session_ids: sessionIds, location }),
    attendance: {
      list: (courseId: number, sessionId: number) =>
        request.get<unknown, ApiResponse<SessionAttendanceItem[]>>(`/courses/${courseId}/session_attendance/`, { params: { session_id: sessionId } }),
      save: (courseId: number, data: { session_id: number; records: Array<{ student: number; status: string; remark?: string; performance?: string }> }) =>
        request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/save_attendance/`, data),
    },
  },
  studentDetail: (courseNo: string): Promise<ApiResponse<{
    course: CourseItem;
    sessions: (CourseSessionItem & { my_attendance: SessionAttendanceItem | null })[];
    homeworks: (HomeworkItem & { my_submission: HomeworkSubmissionItem | null })[];
  }> > => request.get(`/courses/${courseNo}/student_detail/`),
  submitHomework: (courseNo: string, formData: FormData) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseNo}/submit_homework/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateHomeworkSubmission: (courseNo: string, submissionId: number, formData: FormData) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseNo}/update_homework_submission/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  homeworks: {
    list: (courseId: number) => request.get<unknown, ApiResponse<HomeworkItem[]>>(`/courses/${courseId}/homeworks/`),
    create: (courseId: number, data: Partial<HomeworkItem>) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/add_homework/`, data),
    update: (courseId: number, homeworkId: number, data: Partial<HomeworkItem>) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/update_homework/`, { homework_id: homeworkId, ...data }),
    remove: (courseId: number, homeworkId: number) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/remove_homework/`, { homework_id: homeworkId }),
    statistics: (courseId: number, params?: { homework_id?: number }) =>
      request.get<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/homework_statistics/`, { params }),
    submissions: {
      list: (courseId: number, homeworkId: number) =>
        request.get<unknown, ApiResponse<HomeworkSubmissionItem[]>>(`/courses/${courseId}/homework_submissions/`, { params: { homework_id: homeworkId } }),
      batchGrade: (courseId: number, data: { homework_id: number; records: Array<{ submission_id: number; grade: string; comment?: string }> }) =>
        request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/batch_grade_homework/`, data),
      gradeSingle: (courseId: number, data: { submission_id: number; grade: string; comment?: string }) =>
        request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/grade_single/`, data),
    },
  },
  // 课题研究 - 管理端
  research: {
    list: (courseId: number) => request.get<unknown, ApiResponse<ResearchProjectItem[]>>(`/courses/${courseId}/research_projects/`),
    get: (courseId: number, projectId: number) =>
      request.get<unknown, ApiResponse<ResearchProjectItem>>(`/courses/${courseId}/research_projects/${projectId}/`),
    create: (courseId: number, data: Partial<ResearchProjectItem>) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/research_projects/`, data),
    apply: (courseId: number, projectId: number, data: { student_id?: number; team_members?: number[] }) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/research_projects/${projectId}/apply/`, data),
    approve: (courseId: number, projectId: number, data: { student_id: number; action: 'approve' }) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/research_projects/${projectId}/approve_student/`, data),
    reject: (courseId: number, projectId: number, data: { student_id: number; reason?: string }) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/research_projects/${projectId}/reject_student/`, data),
    submitMaterials: (courseId: number, projectId: number, data: {
      phase: string;
      title: string;
      content: string;
      attachment?: File;
    }) => {
      const formData = new FormData();
      formData.append('phase', data.phase);
      formData.append('title', data.title);
      formData.append('content', data.content);
      if (data.attachment) formData.append('attachment', data.attachment);
      return request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/research_projects/${projectId}/submit_material/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    getSubmissions: (courseId: number, params?: Record<string, string | number>) =>
      request.get<unknown, ApiResponse<ResearchSubmissionItem[]>>(`/courses/${courseId}/research_submissions/`, { params }),
    reviewSubmission: (courseId: number, data: {
      submission_id: number;
      action: 'approve' | 'reject';
      review_grade?: string;
      review_comments?: string;
    }) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/review_submission/`, data),
    scoreSummary: (courseId: number, projectId: number): Promise<ApiResponse<{ project: ResearchProjectItem; scores: Record<string, number>; summary: string }> > =>
      request.get(`/courses/${courseId}/research_projects/${projectId}/score_summary/`
      ),
    // 答辩相关
    defenseInfo: (courseId: number, projectId: number): Promise<ApiResponse<{
        id: number;
        presentation_score: number | null;
        content_score: number | null;
        innovation_score: number | null;
        total_score: number | null;
        defense_result: string;
        result_display: string;
        committee_comments: string;
        defense_date?: string;
        defense_location?: string;
        committee_members?: Array<{ id: number; username: string; real_name: string }>;
      }> > => request.get(`/courses/${courseId}/research_projects/${projectId}/defense_info/`),
    setupDefense: (courseId: number, projectId: number, data: {
      defense_date: string;
      defense_location: string;
      committee: number[];
    }) => request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/research_projects/${projectId}/setup_defense/`, data),
    scoreDefense: (courseId: number, projectId: number, data: {
      presentation_score: number;
      content_score: number;
      innovation_score: number;
      comments?: string;
    }): Promise<ApiResponse<{ total_score: number }> > => request.post<unknown, ApiResponse<{ total_score: number }>>(`/courses/${courseId}/research_projects/${projectId}/score_defense/`, data),
  },
  submissions: {
    list: (courseId: number, params?: Record<string, string | number>) =>
      request.get<unknown, ApiResponse<ResearchSubmissionItem[]>>(`/courses/${courseId}/research_submissions/`, { params }),
    review: (courseId: number, data: { submission_id: number; action: string; review_grade?: string; review_comments?: string }) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseId}/review_submission/`, data),
  },
  // 课题研究 - 学生端
  myProjects: (courseNo: string) => request.get<unknown, ApiResponse<ResearchProjectItem[]>>(`/courses/${courseNo}/my_research_projects/`),
  myProjectDetail: (courseNo: string, projectId: number) =>
    request.get<unknown, ApiResponse<ResearchProjectDetailItem>>(`/courses/${courseNo}/my_project_detail/`, { params: { project_id: projectId } }),
  myProjectSubmissions: (courseNo: string, projectId: number) =>
    request.get<unknown, ApiResponse<ResearchSubmissionItem[]>>(`/courses/${courseNo}/my_project_submissions/`, { params: { project_id: projectId } }),
  submitMaterial: (courseNo: string, data: { project_id: number; phase: string; title: string; content: string }) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseNo}/submit_research_material/`, data),
  updateMaterial: (courseNo: string, data: { submission_id: number; phase?: string; title?: string; content?: string }) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>(`/courses/${courseNo}/update_research_material/`, data),
};

export const activityApi = {
  list: (params?: Record<string, string | number | boolean>) =>
    request.get<unknown, PaginatedResponse<ActivityItem>>('/activities/', { params }),
  get: (id: number) => request.get<unknown, ApiResponse<ActivityItem>>(`/activities/${id}/`),
  create: (data: ActivityCreateParams) => request.post<unknown, ApiResponse<Record<string, unknown>>>('/activities/', data),
  update: (id: number, data: Partial<ActivityCreateParams>) =>
    request.patch<unknown, ApiResponse<Record<string, unknown>>>(`/activities/${id}/`, data),
  delete: (id: number) => request.delete<unknown, ApiResponse<Record<string, unknown>>>(`/activities/${id}/`),
  participants: (activityId: number) =>
    request.get<unknown, ApiResponse<ActivityParticipantItem[]>>(`/activities/${activityId}/participants/`),
  addParticipants: (activityId: number, studentIds: number[]) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>(`/activities/${activityId}/add_participants/`, { student_ids: studentIds }),
  removeParticipant: (activityId: number, studentId: number) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>(`/activities/${activityId}/remove_participant/`, { student_id: studentId }),
  attendances: {
    list: (params?: Record<string, string | number>) =>
      request.get<unknown, PaginatedResponse<ActivityAttendanceItem>>('/activities/attendances/', { params }),
    create: (data: Partial<ActivityAttendanceItem>) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>('/activities/attendances/', data),
  },
  submissions: {
    list: (params?: Record<string, string | number>) =>
      request.get<unknown, PaginatedResponse<ActivitySubmissionItem>>('/activities/submissions/', { params }),
    create: (data: Partial<ActivitySubmissionItem>) =>
      request.post<unknown, ApiResponse<Record<string, unknown>>>('/activities/submissions/', data),
  },
};

export const competitionApi = {
  list: (params?: Record<string, string | number | boolean>) =>
    request.get<unknown, PaginatedResponse<CompetitionItem>>('/competitions/', { params }),
  get: (id: number) => request.get<unknown, ApiResponse<CompetitionItem>>(`/competitions/${id}/`),
  create: (data: CompetitionCreateParams) => request.post<unknown, ApiResponse<Record<string, unknown>>>('/competitions/', data),
  update: (id: number, data: Partial<CompetitionCreateParams>) =>
    request.patch<unknown, ApiResponse<Record<string, unknown>>>(`/competitions/${id}/`, data),
  delete: (id: number) => request.delete<unknown, ApiResponse<Record<string, unknown>>>(`/competitions/${id}/`),
  setJudges: (id: number, judgeIds: number[]) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>(`/competitions/${id}/set_judges/`, { judge_ids: judgeIds }),
  participants: (competitionId: number) =>
    request.get<unknown, ApiResponse<CompetitionParticipantItem[]>>(`/competitions/${competitionId}/participants/`),
  addParticipants: (competitionId: number, studentIds: number[]) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>(`/competitions/${competitionId}/add_participants/`, { student_ids: studentIds }),
  removeParticipant: (competitionId: number, studentId: number) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>(`/competitions/${competitionId}/remove_participant/`, { student_id: studentId }),
  getScores: (competitionId: number) =>
    request.get<unknown, ApiResponse<CompetitionScoreItem[]>>(`/competitions/${competitionId}/scores/`),
  recordScores: (competitionId: number, scores: ScoreRecordParams[]) =>
    request.post<unknown, ApiResponse<Record<string, unknown>>>(`/competitions/${competitionId}/record_scores/`, { scores }),
  getMyScores: () =>
    request.get<unknown, ApiResponse<MyCompetitionScoreItem[]>>('/competitions/my_scores/'),
};
